'use client';

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { useMemo } from 'react';
import { AuthMutation, AuthQueryKey, AuthQueryOptions } from '@/api';
import { useAuthStore } from '@/stores/AuthStore';
import { transformCartResponse } from '../_utils/transformCart';
export const useMarketCart = () => {
  const $ = _c(47);
  const queryClient = useQueryClient();
  const isLogin = useAuthStore(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = AuthQueryKey["get:/wello/v2/auth/local-market/cart-items"]();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const queryKey = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = AuthQueryOptions["get:/wello/v2/auth/local-market/cart-items"]();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isLogin) {
    t2 = {
      ...t1,
      enabled: isLogin,
      staleTime: 180000,
      gcTime: 3600000
    };
    $[2] = isLogin;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const cartQuery = useQuery(t2);
  let t3;
  if ($[4] !== queryClient) {
    t3 = debounce(() => {
      queryClient.invalidateQueries({
        queryKey
      });
    }, 300);
    $[4] = queryClient;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const debouncedInvalidate = t3;
  let t4;
  if ($[6] !== queryClient) {
    t4 = {
      onMutate: async () => {
        await queryClient.cancelQueries({
          queryKey
        });
        const previousCart = queryClient.getQueryData(queryKey);
        return {
          previousCart
        };
      },
      onError: (_error, _variables, context) => {
        if (context?.previousCart) {
          queryClient.setQueryData(queryKey, context.previousCart);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey
        });
      }
    };
    $[6] = queryClient;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const mutationOptions = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = AuthMutation["post:/wello/v2/auth/local-market/cart-items"]();
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== mutationOptions) {
    t6 = {
      ...t5,
      ...mutationOptions
    };
    $[9] = mutationOptions;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const addMutation = useMutation(t6);
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = AuthMutation["put:/wello/v2/auth/local-market/cart-items/{cartItemId}/quantity"]();
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== debouncedInvalidate || $[13] !== mutationOptions) {
    t8 = {
      ...t7,
      ...mutationOptions,
      onSettled: debouncedInvalidate
    };
    $[12] = debouncedInvalidate;
    $[13] = mutationOptions;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const updateQuantityMutation = useMutation(t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = AuthMutation["put:/wello/v2/auth/local-market/cart-items/{cartItemId}/option"]();
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== mutationOptions) {
    t10 = {
      ...t9,
      ...mutationOptions
    };
    $[16] = mutationOptions;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const updateOptionsMutation = useMutation(t10);
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = AuthMutation["delete:/wello/v2/auth/local-market/cart-items"]();
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== mutationOptions) {
    t12 = {
      ...t11,
      ...mutationOptions
    };
    $[19] = mutationOptions;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const deleteMutation = useMutation(t12);
  const t13 = cartQuery.data?.context;
  let t14;
  if ($[21] !== t13) {
    t14 = transformCartResponse(t13);
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const shops = t14;
  let t15;
  if ($[23] !== updateQuantityMutation) {
    t15 = async (cartItemId, quantity) => updateQuantityMutation.mutateAsync({
      path: {
        cartItemId
      },
      body: {
        quantity
      }
    });
    $[23] = updateQuantityMutation;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const updateQuantity = t15;
  let t16;
  if ($[25] !== updateOptionsMutation) {
    t16 = async (cartItemId_0, productOptionItemIds) => updateOptionsMutation.mutateAsync({
      path: {
        cartItemId: cartItemId_0
      },
      body: {
        productOptionItemIds
      }
    });
    $[25] = updateOptionsMutation;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const updateOptions = t16;
  let t17;
  if ($[27] !== addMutation) {
    t17 = async params => addMutation.mutateAsync({
      body: params
    });
    $[27] = addMutation;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const addItem = t17;
  let t18;
  if ($[29] !== deleteMutation) {
    t18 = async cartItemIds => deleteMutation.mutateAsync({
      body: {
        cartItemIds
      }
    });
    $[29] = deleteMutation;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const deleteItems = t18;
  let t19;
  if ($[31] !== shops) {
    t19 = shops.reduce(_temp2, 0);
    $[31] = shops;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== addItem || $[34] !== addMutation.isPending || $[35] !== cartQuery.isFetching || $[36] !== cartQuery.isLoading || $[37] !== cartQuery.refetch || $[38] !== deleteItems || $[39] !== deleteMutation.isPending || $[40] !== shops || $[41] !== t19 || $[42] !== updateOptions || $[43] !== updateOptionsMutation.isPending || $[44] !== updateQuantity || $[45] !== updateQuantityMutation.isPending) {
    t20 = {
      shops,
      totalCount: t19,
      isLoading: cartQuery.isLoading,
      isFetching: cartQuery.isFetching,
      addItem,
      isAdding: addMutation.isPending,
      updateQuantity,
      isUpdatingQuantity: updateQuantityMutation.isPending,
      updateOptions,
      isUpdatingOptions: updateOptionsMutation.isPending,
      deleteItems,
      isDeleting: deleteMutation.isPending,
      refetch: cartQuery.refetch
    };
    $[33] = addItem;
    $[34] = addMutation.isPending;
    $[35] = cartQuery.isFetching;
    $[36] = cartQuery.isLoading;
    $[37] = cartQuery.refetch;
    $[38] = deleteItems;
    $[39] = deleteMutation.isPending;
    $[40] = shops;
    $[41] = t19;
    $[42] = updateOptions;
    $[43] = updateOptionsMutation.isPending;
    $[44] = updateQuantity;
    $[45] = updateQuantityMutation.isPending;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  return t20;
};
function _temp(state) {
  return !!state.isLogin;
}
function _temp2(sum, shop) {
  return sum + shop.products.length;
}